import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { COMPANY as C } from "@/lib/company";
import BoatMarquee from "@/components/BoatMarquee";
import Reveal from "@/components/Reveal";
import { ArrowRight, ArrowUpRight, Phone, Mail, MessageCircle } from "lucide-react";

import { MEDIA } from "@/lib/media";

/* Las imágenes y el vídeo se gestionan desde src/lib/media.js (repositorio central) */
const HERO_IMAGE = MEDIA.hero;
const FRIENDS_01 = MEDIA.concept;
const FAMILY_01 = MEDIA.family;
const DETAIL_01 = MEDIA.detail;
const MEDITERRANEAN_01 = MEDIA.mediterranean;

/* --- Paleta editorial LOIRE x Bevela --- */
const DARK = "#20261F";   // verde oliva profundo (fondo oscuro)
const DARK2 = "#2A322A";  // verde oliva secundario
const CREAM = "#F3F0E8";  // crema cálida (fondo claro)

const Eyebrow = ({ children, light }) => (
  <p className="text-[11px] tracking-[0.28em] uppercase font-display" style={{ color: light ? "#9CAA90" : "#7E8A72" }}>
    {children}
  </p>
);

const LinkArrow = ({ to, children, testid, light }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group inline-flex items-center gap-2 text-sm tracking-wide font-display transition-colors"
    style={{ color: light ? CREAM : DARK }}
  >
    <span className="border-b border-current pb-0.5">{children}</span>
    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
  </Link>
);

const PillBtn = ({ to, children, testid, light }) => (
  <Link
    to={to}
    data-testid={testid}
    className="inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-wide font-display border transition-colors"
    style={
      light
        ? { color: CREAM, borderColor: "rgba(243,240,232,0.35)" }
        : { color: DARK, borderColor: "rgba(32,38,31,0.4)" }
    }
    onMouseEnter={(e) => {
      e.currentTarget.style.background = light ? CREAM : DARK;
      e.currentTarget.style.color = light ? DARK : CREAM;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
      e.currentTarget.style.color = light ? CREAM : DARK;
    }}
  >
    {children}
  </Link>
);

export default function Landing() {
  const { t } = useLang();

  const stepImages = [HERO_IMAGE, FRIENDS_01, FAMILY_01, DETAIL_01];

  return (
    <div style={{ background: CREAM }} className="text-[#20261F]">
      {/* 01 — HERO · verde oliva + fotografía editorial */}
      <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden" style={{ background: DARK }} data-testid="landing-hero">
        <video
          className="absolute inset-0 w-full h-full object-cover grayscale opacity-[0.55]"
          src={MEDIA.heroVideo}
          poster={MEDIA.heroPoster}
          autoPlay
          muted
          loop
          playsInline
          data-testid="landing-hero-video"
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${DARK} 4%, rgba(32,38,31,0.35) 45%, rgba(32,38,31,0.55) 100%)` }} />
        <div className="absolute inset-0 max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center">
          <Reveal className="max-w-3xl">
            <Eyebrow light>{t.landing.eyebrow}</Eyebrow>
            <h1 className="mt-6 font-serif font-light text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.94] tracking-tight" style={{ color: CREAM }} data-testid="landing-hero-title">
              {t.landing.concept.signature}
            </h1>
            <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed" style={{ color: "rgba(243,240,232,0.78)" }}>{t.landing.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap items-center gap-8">
              <LinkArrow to="/acceso" testid="landing-hero-cta" light>{t.landing.cta}</LinkArrow>
              <LinkArrow to="/explorar" testid="landing-hero-explore" light>{t.landing.fleet.cta}</LinkArrow>
            </div>
          </Reveal>
        </div>
        {/* tira de miniaturas inferior derecha */}
        <div className="hidden lg:flex absolute bottom-10 right-10 gap-2" data-testid="landing-hero-thumbs">
          {[FRIENDS_01, DETAIL_01, MEDITERRANEAN_01].map((src, i) => (
            <div key={i} className="w-24 h-16 overflow-hidden" style={{ border: "1px solid rgba(243,240,232,0.25)" }}>
              <img src={src} alt="" className="w-full h-full object-cover grayscale opacity-80" />
            </div>
          ))}
        </div>
      </section>

      {/* 02 — CONCEPTO · bloque oscuro, texto + imagen */}
      <section className="py-24 lg:py-32" style={{ background: DARK2 }} data-testid="landing-concept">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <Eyebrow light>{t.landing.concept.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.04]" style={{ color: CREAM }} data-testid="landing-concept-title">
              {t.landing.concept.title}
            </h2>
            <div className="mt-8 max-w-xl space-y-5 text-base lg:text-lg leading-relaxed" style={{ color: "rgba(243,240,232,0.7)" }}>
              <p>{t.landing.concept.body1}</p>
              <p>{t.landing.concept.body2}</p>
            </div>
            <div className="mt-10"><PillBtn to="/acceso" testid="landing-concept-cta" light>{t.landing.cta}</PillBtn></div>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-6">
            <div className="aspect-[4/5] w-full overflow-hidden">
              <img src={FRIENDS_01} alt="" className="w-full h-full object-cover grayscale transition-transform duration-[1400ms] hover:scale-[1.04]" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 03 — FLOTA · crema, "descubre tu próximo barco" */}
      <section className="py-24 lg:py-32" style={{ background: CREAM }} data-testid="landing-fleet">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-end justify-between gap-6 flex-wrap">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t.landing.fleet.eyebrow}</Eyebrow>
            <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" data-testid="landing-fleet-title">{t.landing.fleet.title}</h2>
            <p className="mt-5 max-w-xl leading-relaxed text-[#55604F]">{t.landing.fleet.subtitle}</p>
          </Reveal>
          <Reveal delay={200}><LinkArrow to="/explorar" testid="landing-fleet-cta">{t.landing.fleet.ctaSecondary}</LinkArrow></Reveal>
        </div>
        <BoatMarquee />
      </section>

      {/* 04 — PROCESO · crema, 4 pasos "cómo funciona" */}
      <section className="py-24 lg:py-32 border-t border-[#20261F]/10" style={{ background: CREAM }} data-testid="landing-how">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="max-w-3xl">
            <Eyebrow>{t.landing.how.eyebrow}</Eyebrow>
            <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.04]" data-testid="landing-how-title">{t.landing.how.title}</h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
            {t.landing.how.steps.map((s, i) => (
              <Reveal key={s.k} delay={i * 120} testid={`landing-how-step-${i}`}>
                <div className="aspect-[4/3] w-full overflow-hidden mb-6">
                  <img src={stepImages[i]} alt="" className="w-full h-full object-cover grayscale transition-transform duration-[1200ms] hover:scale-[1.05]" />
                </div>
                <p className="font-display text-xs tracking-[0.24em] text-[#7E8A72]">{s.k}</p>
                <h3 className="mt-3 font-serif text-xl leading-snug">{s.h}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#55604F]">{s.p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 05 — QUÉ HACE BEVELA · bloque oscuro, texto + imagen + pasos */}
      <section className="py-24 lg:py-32" style={{ background: DARK }} data-testid="landing-what">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <Reveal className="lg:col-span-7">
              <Eyebrow light>{t.landing.what.eyebrow}</Eyebrow>
              <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.04]" style={{ color: CREAM }} data-testid="landing-what-title">
                {t.landing.what.title}
              </h2>
              <p className="mt-6 max-w-xl leading-relaxed" style={{ color: "rgba(243,240,232,0.7)" }}>{t.landing.what.subtitle}</p>
            </Reveal>
            <Reveal delay={200} className="lg:col-span-5">
              <div className="aspect-[4/5] w-full overflow-hidden">
                <img src={FAMILY_01} alt="" className="w-full h-full object-cover grayscale transition-transform duration-[1400ms] hover:scale-[1.04]" />
              </div>
            </Reveal>
          </div>
          <div className="mt-16 grid md:grid-cols-2 gap-x-14 gap-y-12">
            {t.landing.what.steps.map((s, i) => (
              <Reveal key={s.k} delay={i * 120} className="flex items-start gap-7 border-t pt-6" testid={`landing-what-step-${i}`}>
                <span className="font-serif text-4xl lg:text-5xl leading-none shrink-0 w-14" style={{ color: "rgba(156,170,144,0.5)" }}>{s.k}</span>
                <div>
                  <h3 className="font-serif text-2xl leading-snug" style={{ color: CREAM }}>{s.h}</h3>
                  <p className="mt-3 leading-relaxed" style={{ color: "rgba(243,240,232,0.65)" }}>{s.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 06 — ESTADÍSTICA + MODALIDADES · crema, número editorial */}
      <section className="py-24 lg:py-32" style={{ background: CREAM }} data-testid="landing-stat">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-5">
            <p className="font-serif font-light text-[8rem] sm:text-[10rem] lg:text-[12rem] leading-[0.82] tracking-tight" data-testid="landing-stat-number">{t.landing.stat.number}</p>
            <p className="mt-6 pt-5 border-t border-[#20261F]/20 max-w-[300px] font-serif text-2xl leading-snug">{t.landing.stat.line1}</p>
          </Reveal>
          <Reveal delay={180} className="lg:col-span-7 lg:pl-10">
            <p className="text-base lg:text-lg leading-relaxed max-w-2xl text-[#55604F]">{t.landing.stat.body}</p>
            <div className="mt-10 pl-6 border-l border-[#20261F]">
              <p className="font-serif text-2xl lg:text-3xl leading-snug">{t.landing.stat.outro1}</p>
              <p className="font-serif text-2xl lg:text-3xl text-[#9CAA90] leading-snug">{t.landing.stat.outro2}</p>
            </div>
          </Reveal>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 grid md:grid-cols-2 gap-6">
          <Reveal delay={100}>
            <article className="p-10 lg:p-12 h-full border border-[#20261F]/12" data-testid="landing-ways-private">
              <Eyebrow>{t.landing.ways.privateTag}</Eyebrow>
              <h3 className="mt-4 font-serif text-3xl lg:text-4xl leading-tight">{t.landing.ways.privateTitle}</h3>
              <p className="mt-6 leading-relaxed text-[#55604F]">{t.landing.ways.privateBody}</p>
            </article>
          </Reveal>
          <Reveal delay={220}>
            <article className="p-10 lg:p-12 h-full" style={{ background: DARK }} data-testid="landing-ways-hybrid">
              <Eyebrow light>{t.landing.ways.hybridTag}</Eyebrow>
              <h3 className="mt-4 font-serif text-3xl lg:text-4xl leading-tight" style={{ color: CREAM }}>{t.landing.ways.hybridTitle}</h3>
              <p className="mt-6 leading-relaxed" style={{ color: "rgba(243,240,232,0.7)" }}>{t.landing.ways.hybridBody}</p>
            </article>
          </Reveal>
        </div>
      </section>

      {/* 07 — COMPARATIVA · crema, tabla */}
      <section className="py-24 lg:py-32 border-t border-[#20261F]/10" style={{ background: CREAM }} data-testid="landing-compare">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>{t.landing.compare.eyebrow}</Eyebrow>
            <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.04]" data-testid="landing-compare-title">{t.landing.compare.title}</h2>
            <p className="mt-5 leading-relaxed text-[#55604F]">{t.landing.compare.subtitle}</p>
            <p className="mt-8 font-display text-sm font-semibold">{t.landing.compare.example}</p>
          </Reveal>
          <Reveal delay={180} className="mt-10 overflow-x-auto -mx-6 lg:mx-0">
            <div className="min-w-[720px] lg:min-w-0 px-6 lg:px-0" data-testid="landing-compare-table">
              <div className="grid grid-cols-4 border-t border-b border-[#20261F]/20 py-4">
                <span></span>
                {t.landing.compare.cols.map((c, i) => (<span key={c} className={`text-xs font-display uppercase tracking-widest ${i === 2 ? "text-[#20261F]" : "text-[#7E8A72]"}`}>{c}</span>))}
              </div>
              {t.landing.compare.rows.map((row, ri) => (
                <div key={ri} className="grid grid-cols-4 py-4 border-b border-[#20261F]/10 text-sm">
                  <span className="text-[#55604F] pr-4">{row[0]}</span>
                  <span className="text-[#55604F] font-display">{row[1]}</span>
                  <span className="text-[#55604F] font-display">{row[2]}</span>
                  <span className="text-[#20261F] font-display font-medium">{row[3]}</span>
                </div>
              ))}
              <div className="grid grid-cols-4 py-6 px-3" style={{ background: DARK, color: CREAM }}>
                <span className="font-serif text-lg pl-3">{t.landing.compare.totalLabel}</span>
                <span className="font-serif text-lg" style={{ color: "rgba(243,240,232,0.55)" }}>{t.landing.compare.totalValues[0]}</span>
                <span className="font-serif text-lg" style={{ color: "rgba(243,240,232,0.55)" }}>{t.landing.compare.totalValues[1]}</span>
                <span className="font-serif text-2xl" data-testid="landing-compare-total-bevela">{t.landing.compare.totalValues[2]}</span>
              </div>
            </div>
          </Reveal>
          <div className="mt-14 grid lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-5">
              <Eyebrow>{t.landing.compare.keyTitle}</Eyebrow>
              <h3 className="mt-3 font-serif text-2xl lg:text-3xl leading-snug">{t.landing.compare.ctaEyebrow}</h3>
              <div className="mt-8"><PillBtn to="/acceso" testid="landing-compare-cta">{t.landing.compare.cta}</PillBtn></div>
            </Reveal>
            <Reveal delay={180} className="lg:col-span-7 space-y-3 leading-relaxed text-[#55604F]">
              <p>{t.landing.compare.keyBody1}</p>
              <p>{t.landing.compare.keyBody2}</p>
              <p className="font-display text-[#20261F]">{t.landing.compare.keyBody3}</p>
              <p>{t.landing.compare.keyBody4}</p>
            </Reveal>
          </div>
          <p className="mt-14 text-xs text-[#7E8A72] leading-relaxed max-w-4xl">{t.landing.compare.disclaimer}</p>
        </div>
      </section>

      {/* 08 — TESTIMONIOS · crema */}
      <section className="py-24 lg:py-32 border-t border-[#20261F]/10" style={{ background: CREAM }} data-testid="landing-social">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t.social.eyebrow}</Eyebrow>
            <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl leading-tight">{t.social.title}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {t.social.items.map((it, i) => (
              <Reveal key={it.name} delay={i * 120} testid={`landing-testimonial-${i}`} className="p-8 h-full flex flex-col border border-[#20261F]/12">
                <p className="font-serif text-xl leading-snug flex-1">“{it.quote}”</p>
                <div className="mt-8 pt-5 border-t border-[#20261F]/15">
                  <p className="font-display text-sm font-semibold">{it.name}</p>
                  <p className="text-xs text-[#7E8A72] font-display tracking-wide mt-0.5">{it.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 09 — CIERRE · bloque oscuro con CTA e imagen */}
      <section className="relative overflow-hidden" style={{ background: DARK }} data-testid="landing-close">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-36 grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-7">
            <Eyebrow light>{t.landing.close.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.04]" style={{ color: CREAM }} data-testid="landing-close-title">{t.landing.close.title}</h2>
            <p className="mt-8 text-base lg:text-lg leading-relaxed max-w-xl" style={{ color: "rgba(243,240,232,0.75)" }}>{t.landing.close.line1}</p>
            <p className="mt-3 font-display text-sm" style={{ color: "rgba(243,240,232,0.5)" }}>{t.landing.close.line2}</p>
            <div className="mt-10 flex flex-wrap items-center gap-8">
              <LinkArrow to="/acceso" testid="landing-close-cta" light>{t.landing.cta}</LinkArrow>
              <LinkArrow to="/por-que-bevela" testid="landing-close-about" light>{t.footer.about}</LinkArrow>
            </div>
            <div className="mt-12 flex flex-wrap gap-3">
              <a href={`tel:${C.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 px-5 py-3 text-sm font-display rounded-full" style={{ border: "1px solid rgba(243,240,232,0.25)", color: CREAM }} data-testid="landing-contact-phone"><Phone className="h-4 w-4" />{C.phone}</a>
              <a href={`mailto:${C.email}`} className="flex items-center gap-2 px-5 py-3 text-sm font-display rounded-full" style={{ border: "1px solid rgba(243,240,232,0.25)", color: CREAM }} data-testid="landing-contact-email"><Mail className="h-4 w-4" />{C.email}</a>
              <a href={`https://wa.me/${C.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 text-sm font-display rounded-full" style={{ border: "1px solid rgba(243,240,232,0.25)", color: CREAM }} data-testid="landing-contact-whatsapp"><MessageCircle className="h-4 w-4" />{t.contactStrip.whatsapp}</a>
            </div>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-5">
            <div className="aspect-[4/5] w-full overflow-hidden">
              <img src={DETAIL_01} alt="" className="w-full h-full object-cover grayscale opacity-90" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
